import {AfterViewInit, Component, inject, NgZone, OnDestroy} from '@angular/core';
import {FlexModule} from '@angular/flex-layout';
import {Router} from '@angular/router';
import {AuthenticationModule, AuthenticationService} from '@modules/authentication';
import {AuthenticationConfigurationService} from '@modules/authentication/core/config/authentication-configuration.service';
import {LoginFormComponent} from '@modules/authentication/core/login/login-form/login-form.component';
import {LoginLogoComponent} from '@modules/authentication/core/login/login-logo/login-logo.component';
import {TranslateModule} from '@ngx-translate/core';
import {FuseDirectivesModule} from 'fuse-core/directives/directives';
import {MatButtonModule} from '@angular/material/button';
import {ScriptLoaderService} from '../script-loader-service.service';
import {get} from 'lodash-es';
import {MatIcon} from "@angular/material/icon";
import {brand} from "../../../../../../src/app/settings/sapiens";
import {take} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import { NgClass } from "@angular/common";
import {RegisterLinkComponent} from '@modules/authentication/core/login/register-link/register-link.component';
import {buildUrl} from '../../../../../../src/environments/environment';
import {AppleAuthResult, AppleAuthService} from './apple-auth.service';
import {FacebookAuthResult, FacebookAuthService} from './facebook-auth.service';

declare const google: any;

@Component({
    selector: 'app-login',
    imports: [
    FlexModule,
    TranslateModule,
    LoginFormComponent,
    LoginLogoComponent,
    FuseDirectivesModule,
    AuthenticationModule,
    MatButtonModule,
    MatIcon,
    NgClass,
    RegisterLinkComponent
],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements AfterViewInit, OnDestroy {
    private configuration = inject(AuthenticationConfigurationService);
    private authenticationService = inject(AuthenticationService);
    private router = inject(Router);
    private ngZone = inject(NgZone);
    private scriptLoaderService = inject(ScriptLoaderService);
    private appleAuthService: AppleAuthService = inject(AppleAuthService);
    private facebookAuthService: FacebookAuthService = inject(FacebookAuthService);
    public settings = this.configuration.settings;
    protected readonly assetbrand = brand;
    public isEndOfFreeDemo = localStorage.getItem('isEndOfFreeDemo') === 'true';
    private appleAuthSubscription?: Subscription;
    private facebookAuthSubscription?: Subscription;
    private googleIframeFocusWatcherInterval: ReturnType<typeof setInterval>;
    private lastGoogleIframeFocused = false;

    ngAfterViewInit(): void {
        if (!this.activateAuthenticationByThirdParty) {
            return;
        }

        if (this.googleClientId) {
            this.scriptLoaderService.loadGoogleScript()
                .then(() => {
                    this.googleCallBack();
                    this.initGoogleButtonAndCallBackSettings();
                })
                .catch((error) => {
                    console.error('Google script failed to load', error);
                });
            this.startGoogleIframeFocusWatcher();
        } else {
            console.warn('Google sign-in disabled: missing googleClientId setting.');
        }

        if (this.appleClientId && this.appleRedirectUri) {
            this.appleAuthSubscription = this.appleAuthService.initialize({
                clientId: this.appleClientId,
                redirectUri: this.appleRedirectUri,
                buttonId: 'appleid-signin'
            }).subscribe((result: AppleAuthResult) => {
                if (result.token) {
                    this.connect(result.token);
                } else if (result.error) {
                    console.error('Apple sign-in error', result.error);
                }
            });
        }

        this.initFacebookLayer();
    }

    private initFacebookLayer(): void {
        if (this.activateAuthenticationByThirdParty && this.facebookAppId && !this.facebookAuthSubscription) {
            this.facebookAuthSubscription = this.facebookAuthService.initialize({
                appId: this.facebookAppId,
                buttonId: 'facebook-signin-button' // On le laisse pour la config, mais on ne s'en sert plus pour le clic
            }).subscribe((result: FacebookAuthResult) => {
                if (result.token) this.connect(result.token);
                else if (result.error) console.error('Facebook sign-in error', result.error);
            });
        }
    }

    public signInWithFacebook(): void {
        this.facebookAuthService.login();
    }

    ngOnDestroy(): void {
        this.appleAuthSubscription?.unsubscribe();
        this.appleAuthService.dispose();
        this.facebookAuthSubscription?.unsubscribe();
        this.facebookAuthService.dispose();
        if (this.googleIframeFocusWatcherInterval) {
            clearInterval(this.googleIframeFocusWatcherInterval);
        }
    }

    /* GOOGLE */
    private googleCallBack() {
        // Définir la callback globale que Google appelle après login
        (window as any).handleCredentialResponse = (response: any) => {
            this.ngZone.run(() => {
                // token google a envoyer au back pour que le back se connecte et renvoi un jwt au front
                const idToken = response.credential;
                this.authenticationService.loginWithGoogle(idToken).pipe(take(1))
                    .subscribe({
                        next: (res) => {
                            this.connect(res?.token);
                        },
                        error: (err) => {
                            console.error('Erreur google connexion:', err);
                        }
                    });
            });
        };
    }

    private initGoogleButtonAndCallBackSettings() {
        google.accounts.id.initialize({
            client_id: this.googleClientId,
            callback: (window as any).handleCredentialResponse
        });

        google.accounts.id.renderButton(
            document.getElementById('google-signin-button'),
            {theme: 'outline', size: 'large'}
        );
    }

    private connect(token: string) {
        this.authenticationService.setTokenData(token);
        this.authenticationService.loginWithToken(token).pipe(take(1)).subscribe({
            next: () => {
                this.authenticationService.navigateAfterLogin();
            },
            error: (err) => {
                console.error('Erreur loginWithToken', err);
            }
        });
    }

    public onOpenDemoClick() {
        this.router.navigateByUrl('/demo');
    }

    public get googleClientId(): string {
        return get(this.settings, 'googleClientId', '');
    }

    public get activateAuthenticationByThirdParty(): boolean {
        return get(this.settings, 'activateAuthenticationByThirdParty', false);
    }

    public get appleClientId(): string {
        return get(this.settings, 'appleClientId', '');
    }

    public get appleRedirectUri(): string {
        const configured = get(this.settings, 'appleRedirectUri', '');
        if (!configured) {
            return '';
        }

        if (/^https?:\/\//i.test(configured)) {
            return configured;
        }

        const baseUrl = buildUrl(this.assetbrand);
        const sanitizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        const sanitizedPath = configured.startsWith('/') ? configured : `/${configured}`;
        return `${sanitizedBase}${sanitizedPath}`;
    }

    public get facebookAppId(): string {
        return get(this.settings, 'facebookAppId', '');
    }

    // Chrome browser specific issue: since Google "Sign in" button is embedded in an iframe, we need to check if that iframe is active in order to know if the button is focused
    public isGoogleIframeFocused(): boolean {
        const iframe = document.querySelector('#google-signin-button iframe') as HTMLIFrameElement | null;
        return !!iframe && document.activeElement === iframe;
    }

    private startGoogleIframeFocusWatcher(): void {
        this.ngZone.runOutsideAngular(() => {
            this.googleIframeFocusWatcherInterval = setInterval(() => {
                const now = this.isGoogleIframeFocused();
                if (now !== this.lastGoogleIframeFocused) {
                    this.lastGoogleIframeFocused = now;
                    this.ngZone.run(() => {});
                }
            }, 150);
        });
    }
}
